/**
 * Phase 3: Secure Auth Provider
 *
 * Access token: short-lived (15 min), memory-only — never in localStorage.
 * Refresh token: long-lived (30 days), HttpOnly Secure cookie — browser-managed.
 * On page load: attempt refresh (cookie is sent automatically).
 * On token expiry: automatic silent refresh via API client.
 * Property selection: persisted in localStorage (non-sensitive).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import type { ReactNode } from "react"
import { apiClient, setAccessToken, clearAccessToken, setOnRefreshCallback } from "@/lib/api"
import { identifyUser, resetUser } from "@/lib/posthog"

interface User {
  id: string
  easy_id: string
  email: string
  name: string
  role: string
  is_platform_admin?: boolean
  is_ops_admin?: boolean
}

interface Property {
  id: string
  name: string
  pms_provider?: string
  address?: string
  timezone?: string
  cover_image_url?: string
}

interface AuthState {
  user: User | null
  properties: Property[]
  activePropertyId: string
  activeProperty: Property | null
  isAuthenticated: boolean
  isLoading: boolean
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  switchProperty: (propertyId: string) => Promise<void>
  refreshSession: () => Promise<void>
  isPlatformAdmin: boolean
  isOpsAdmin: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Only property selection is persisted (non-sensitive)
const PROPERTY_KEY = "sf_property"

function getStoredPropertyId(): string {
  try {
    return localStorage.getItem(PROPERTY_KEY) || ""
  } catch {
    return ""
  }
}

function setStoredPropertyId(id: string) {
  try {
    if (id) {
      localStorage.setItem(PROPERTY_KEY, id)
    } else {
      localStorage.removeItem(PROPERTY_KEY)
    }
  } catch {}
}

// Phase 3: Remove legacy token from localStorage on first load
function cleanupLegacyToken() {
  try {
    localStorage.removeItem("sf_token")
  } catch {}
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    properties: [],
    activePropertyId: "",
    activeProperty: null,
    isAuthenticated: false,
    isLoading: true,
  })

  // Register callback for when the API client automatically refreshes
  useEffect(() => {
    setOnRefreshCallback((data) => {
      setState((s) => {
        if (!s.isAuthenticated) return s
        const properties = (data.properties as Property[]) || s.properties
        const activeId = data.property_id || s.activePropertyId
        const activeProp = properties.find((p) => p.id === activeId) || s.activeProperty
        return {
          ...s,
          user: (data.user as User) ?? s.user,
          properties,
          activePropertyId: activeId,
          activeProperty: activeProp,
        }
      })
    })

    return () => setOnRefreshCallback(null)
  }, [])

  // Bootstrap: attempt session restore via refresh token (HttpOnly cookie)
  useEffect(() => {
    cleanupLegacyToken()

    // Try refresh first — the HttpOnly cookie is sent automatically
    apiClient.refresh().then((refreshed) => {
      if (refreshed) {
        // Refresh succeeded — now get full session data
        return apiClient.getSession().then((res) => {
          if (res.ok && res.logged_in) {
            const storedProp = getStoredPropertyId()
            const activeId = storedProp && res.properties?.some((p: Property) => p.id === storedProp)
              ? storedProp
              : res.property_id || ""
            const activeProp = res.properties?.find((p: Property) => p.id === activeId) || null

            setState({
              user: res.user ?? null,
              properties: (res.properties as Property[]) || [],
              activePropertyId: activeId,
              activeProperty: activeProp,
              isAuthenticated: true,
              isLoading: false,
            })

            // PostHog: identify the restored session user
            if (res.user) identifyUser({ id: res.user.id, email: res.user.email, name: res.user.name })

            if (activeId) setStoredPropertyId(activeId)
          } else {
            setState((s) => ({ ...s, isLoading: false }))
          }
        })
      }
      // No refresh token or refresh failed — user must log in
      setState((s) => ({ ...s, isLoading: false }))
    }).catch(() => {
      setState((s) => ({ ...s, isLoading: false }))
    })
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiClient.login(email, password)
    if (!res.ok) {
      throw new Error(res.error || "Login failed")
    }

    // Store access token in memory only
    if (res.access_token) {
      setAccessToken(res.access_token, res.access_token_expires_at ?? null)
    } else if (res.token) {
      // Legacy fallback
      setAccessToken(res.token, null)
    }

    const activeId = res.property_id || (res.properties?.[0]?.id ?? "")
    const activeProp = res.properties?.find((p: Property) => p.id === activeId) || null

    setState({
      user: res.user ?? null,
      properties: (res.properties as Property[]) || [],
      activePropertyId: activeId,
      activeProperty: activeProp,
      isAuthenticated: true,
      isLoading: false,
    })

    // PostHog: identify the logged-in user
    if (res.user) identifyUser({ id: res.user.id, email: res.user.email, name: res.user.name })

    if (activeId) setStoredPropertyId(activeId)
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiClient.logout()
    } catch {}

    clearAccessToken()
    setStoredPropertyId("")
    resetUser()

    setState({
      user: null,
      properties: [],
      activePropertyId: "",
      activeProperty: null,
      isAuthenticated: false,
      isLoading: false,
    })
  }, [])

  const switchProperty = useCallback(async (propertyId: string) => {
    const res = await apiClient.switchProperty(propertyId)
    if (!res.ok) {
      throw new Error(res.error || "Failed to switch property")
    }

    const activeId = res.property_id || propertyId
    const activeProp = ((res.properties as Property[]) || state.properties).find((p) => p.id === activeId) || null

    setState((s) => ({
      ...s,
      activePropertyId: activeId,
      activeProperty: activeProp,
      properties: (res.properties as Property[]) || s.properties,
    }))

    setStoredPropertyId(activeId)
  }, [state.properties])

  const refreshSession = useCallback(async () => {
    const res = await apiClient.getSession()
    if (res.ok && res.logged_in) {
      const properties = (res.properties as Property[]) || []
      const activeId = res.property_id || (properties[0]?.id ?? "")
      const activeProp = properties.find((p) => p.id === activeId) || null

      setState((s) => ({
        ...s,
        user: (res.user as User) ?? s.user,
        properties,
        activePropertyId: activeId,
        activeProperty: activeProp,
      }))

      if (activeId) setStoredPropertyId(activeId)
    }
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    ...state,
    login,
    logout,
    switchProperty,
    refreshSession,
    isPlatformAdmin: Boolean(state.user?.is_platform_admin),
    isOpsAdmin: Boolean(state.user?.is_ops_admin || state.user?.role === "admin" || state.user?.role === "ops"),
  }), [state, login, logout, switchProperty, refreshSession])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}
